import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  Autocomplete,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  billAsTypes,
  ServiceCodes,
  Segment,
  StandardJobSearchResult,
} from "../../QueueBoard/models/workOrderTypes";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { clearCurrentSegment } from "../../store/queueBoardSlice";
import "./WorkOrderSegmentUpdate.css";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import {
  ButtonStyle,
  ButtonStyleOutlined,
  searchListItem,
  standardJobSearchResultStyle,
} from "../../QueueBoard/models/workOrderStyles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { SegmentApi } from "./api/segmentApi";
import { useLoading } from "components/common/context/LoadingContext";
import { truncateName } from "components/common/Utils/commonUtilityFunctions";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import localStorageHelper from "../../../app/localStorage";
import { User } from "components/authentication/models/authTypes";
import packageJson from "../../../../package.json";
import { useSnackbar } from "notistack";
import { ensureEditableWorkOrder } from "../utils/finalizationHelpers";
// NOTE: In Yup 1.x, `string()` and `number()` reject `null` unless `.nullable()` is
// explicitly set. Existing segments coming from the API frequently contain `null`
// for optional fields (memo, stdJobCode, standardJobSteps, etc.). Without the
// `.nullable()` calls below, validation fails silently on submit and Formik never
// invokes `onSubmit`, which is why the Update Segment button appeared to "do nothing".
const SegmentSchema = Yup.object().shape({
  displayText: Yup.string().required("Segment title is required"),
  serviceCodeIdentity: Yup.number()
    .typeError("Service code is required")
    .required("Service code is required"),
  memo: Yup.string().nullable(),
  serviceValue: Yup.number()
    .typeError("Service value must be a number")
    .nullable(),
  serviceType: Yup.string().nullable(),
  stdJobCode: Yup.string().nullable(),
  stdJobDescription: Yup.string().nullable(),
  stdJobHeaderId: Yup.number().nullable(),
  stdJobRecommendedHrs: Yup.string().nullable(),
  stdJobSource: Yup.string().nullable(),
  stdJobSteps: Yup.string().nullable(),
  standardJobSteps: Yup.string().nullable(),
  StdJobUnitModelId: Yup.number().nullable(),
  ShopSuppAmt: Yup.string().nullable(),
  shopSuppAmt: Yup.string().nullable(),
});

interface WorkOrderSegmentUpdateProps {
  currentSegment: Segment;
  open: boolean;
  handleCloseEx: () => void;
  update: (values: Segment) => void;
  invoiceDocumentIdentity?: number;
}

const WorkOrderSegmentUpdate = ({
  currentSegment,
  open,
  handleCloseEx,
  update,
  invoiceDocumentIdentity,
}: WorkOrderSegmentUpdateProps) => {
  const theme = useTheme();
  const currentSeg: Segment = {
    ...currentSegment,
    stdJobSource: "manual",
  };
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );
  const { serviceCodes, stdJobSources, standardJobCodes } = useWorkOrderStore();
  const [placeHolderForAmount] = useState("Estimated Labor Hours");
  const [stdJobSearchTerm, setStdJobSearchTerm] = useState("");
  const [showList, setShowList] = useState(false);
  const [stdJobResult, setStdJobResult] = useState<StandardJobSearchResult[]>(
    [],
  );
  const [textForAmount, setTextForHours] = useState("Estimated Labor Hours");
  const [stdJobSource] = useState("manual");
  const { showLoading, hideLoading } = useLoading();
  const containerRef = useRef<HTMLDivElement>(null);
  const sortedStandardJobCodes = [...standardJobCodes].sort((a, b) =>
    a.value.localeCompare(b.value),
  );

  const [selectedJobCode, setSelectedJobCode] = useState(
    sortedStandardJobCodes[0].key,
  );
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values) => {
    // eslint-disable-next-line no-debugger
    debugger;
    const canModify = await ensureEditableWorkOrder(
      invoiceDocumentIdentity,
      enqueueSnackbar,
    );
    if (!canModify) {
      return;
    }
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "segmentUpdated",
      {
        elapsed_ms: "124",
        title: "Segment Update",
        componentName: "WorkOrderSegmentUpdate.tsx",
        version: packageJson.version,
      },
    );
    update(values); // Update the existing segment
    dispatch(clearCurrentSegment());
    handleCloseEx();
  };
  const handleClose = () => {
    // eslint-disable-next-line no-debugger
    debugger;
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "closeUpdateSegmentDialog",
      {
        elapsed_ms: "124",
        title: "Segment Update",
        componentName: "WorkOrderSegmentUpdate.tsx",
        version: packageJson.version,
      },
    );
    handleCloseEx(); // Call the function passed from parent
  };

  const handleSearch = async () => {
    showLoading();
    try {
      const response = await SegmentApi.SearchStandardJobStep(
        stdJobSource,
        selectedJobCode,
        stdJobSearchTerm,
      );
      if (response === null) setStdJobResult([]);
      else {
        setStdJobResult(response);
        setShowList(true);
      }
    } catch (error) {
      console.error("Error fetching standard jobs:", error);
    } finally {
      hideLoading();
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "searchStandardJobStep",
      {
        elapsed_ms: "124",
        title: "Segment Update",
        componentName: "WorkOrderSegmentUpdate.tsx",
        version: packageJson.version,
      },
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const setFormikFieldValue = async (
    formik: any,
    fieldName: string,
    fieldValue: any,
  ) => {
    await formik.setFieldValue(fieldName, fieldValue, true);
    await formik.setFieldTouched(fieldName, true, true);
    await formik.validateForm(); // Validate after setting all fields
  };
  const handleStandardJobSelection = async (
    stdJobStep: StandardJobSearchResult,
    formik: any,
  ) => {
    await setFormikFieldValue(formik, "stdJobCode", stdJobStep.jobCode);
    await setFormikFieldValue(formik, "memo", stdJobStep.jobDescription);
    await setFormikFieldValue(
      formik,
      "stdJobHeaderId",
      stdJobStep.stdJobHeaderId,
    );
    await setFormikFieldValue(
      formik,
      "stdJobRecommendedHrs",
      stdJobStep.durationHours,
    );
    await setFormikFieldValue(formik, "stdJobSource", stdJobStep.source);
    await setFormikFieldValue(formik, "standardJobSteps", stdJobStep.jobSteps);
    await setFormikFieldValue(
      formik,
      "StdJobUnitModelId",
      stdJobStep.stdJobUnitModelId,
    );
    await setFormikFieldValue(
      formik,
      "actualOrFlatRateLaborHrs",
      stdJobStep.durationHours,
    );

    setStdJobSearchTerm(stdJobStep.jobCode);
    setStdJobResult([]);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "selectStandardJobStep",
      {
        elapsed_ms: "124",
        title: "Segment Update",
        componentName: "WorkOrderSegmentUpdate.tsx",
        version: packageJson.version,
      },
    );
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (currentSegment.stdJobCode) {
      setStdJobSearchTerm(currentSegment.stdJobCode);
    }
  }, [currentSegment.stdJobCode, open]);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
          })}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              pl="15px"
              sx={{
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "16px",
              }}
            >
              Update Segment
            </Typography>
            <IconButton
              sx={{ color: theme.palette.primary.contrastText }}
              onClick={handleClose}
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Formik
            initialValues={currentSeg}
            validationSchema={SegmentSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form>
                {/* Display Text */}
                <Grid container spacing={2} sx={{ paddingTop: "10px" }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Display Text"
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="displayText"
                      inputProps={{ maxLength: 15 }}
                      value={formik.values.displayText}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.displayText &&
                        !!formik.errors.displayText
                      }
                    />
                  </Grid>
                  {/* Service Code */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <Autocomplete
                        options={serviceCodes}
                        size="small"
                        getOptionLabel={(option: ServiceCodes) =>
                          option.serviceCode.trim()
                        }
                        value={
                          serviceCodes.find(
                            (service) =>
                              Number(service.identity) ===
                              formik.values.serviceCodeIdentity,
                          ) || null
                        }
                        id="serviceCodeIdentity"
                        onChange={(event, newValue) => {
                          formik.setFieldValue(
                            "serviceCodeIdentity",
                            newValue?.identity || null,
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            error={
                              !!(
                                formik.touched.serviceCodeIdentity &&
                                formik.errors.serviceCodeIdentity
                              )
                            }
                            {...params}
                            label="Service Code*"
                            variant="outlined"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* Bill As */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <InputLabel>Bill As</InputLabel>
                      <Select
                        name="billAs"
                        label="Bill As"
                        value={formik.values.billAs}
                        onChange={(event) => {
                          //
                          formik.setFieldValue("billAs", event.target.value);
                          if (event.target.value === "actual") {
                            setTextForHours("Estimated Labor Hours");
                          } else {
                            setTextForHours("Flat Rate Labor Hours");
                          }
                        }}
                      >
                        {billAsTypes.map((billAsObj) => (
                          <MenuItem key={billAsObj.key} value={billAsObj.key}>
                            {billAsObj.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Flat rate labor hours */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        size="small"
                        label={textForAmount}
                        name="actualOrFlatRateLaborHrs"
                        type="number"
                        value={formik.values.actualOrFlatRateLaborHrs ?? 0.0}
                        onChange={formik.handleChange}
                        onFocus={(event) => {
                          if (Number(event.target.value) === 0) {
                            formik.setFieldValue(
                              "actualOrFlatRateLaborHrs",
                              "",
                            );
                          }
                        }}
                        onBlur={(event) => {
                          if (
                            event.target.value === "" ||
                            event.target.value === null
                          ) {
                            formik.setFieldValue("actualOrFlatRateLaborHrs", 0);
                          }
                          formik.handleBlur(event);
                        }}
                        placeholder={placeHolderForAmount}
                        inputProps={{ min: "0", step: "0.01" }} // Prevents negative values
                      />
                    </FormControl>
                  </Grid>

                  {/* Standard Job Source */}
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <InputLabel>Job Source</InputLabel>
                      <Select
                        name="stdJobSource"
                        label="Std Job Source"
                        value={formik.values.stdJobSource || ""}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "stdJobSource",
                            event.target.value,
                          );
                        }}
                      >
                        {stdJobSources.map((source, index) => (
                          <MenuItem key={index} value={source}>
                            {source}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Standard Job Search Type */}
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                    >
                      <InputLabel>Std Search Type</InputLabel>
                      <Select
                        value={selectedJobCode}
                        onChange={(e) => setSelectedJobCode(e.target.value)}
                        label="Std Search Type"
                      >
                        {sortedStandardJobCodes.map((code) => (
                          <MenuItem key={code.key} value={code.key}>
                            {code.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Standard Job Code Search */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: "10%" }}
                      ref={containerRef}
                    >
                      {/* <InputLabel>Enter min 3 characters</InputLabel> */}
                      <TextField
                        fullWidth
                        inputProps={{ minLength: 3, maxLength: 75 }}
                        label="Enter min 3 characters"
                        size="small"
                        name="stdJobCode"
                        variant="outlined"
                        value={stdJobSearchTerm || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setStdJobSearchTerm(value);

                          if (value.trim() === "") {
                            setStdJobResult([]); //  clear results
                            setShowList(false); //  hide the list
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            // Prevent Formik from submitting the whole segment
                            // form when the user just wants to trigger a
                            // standard-job search via Enter.
                            e.preventDefault();
                            if (stdJobSearchTerm.trim().length >= 3) {
                              handleSearch();
                            }
                          }
                        }}
                        onFocus={() => {
                          if (
                            stdJobSearchTerm.trim().length > 0 &&
                            stdJobResult.length > 0
                          ) {
                            setShowList(true);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                disabled={
                                  !stdJobSearchTerm ||
                                  stdJobSearchTerm.trim().length < 3
                                }
                                onClick={handleSearch}
                                edge="end"
                                aria-label="search"
                              >
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {stdJobResult.length > 0 && showList && (
                        <List sx={standardJobSearchResultStyle(theme)}>
                          {stdJobResult.map(
                            (item: StandardJobSearchResult, index) => (
                              <ListItem
                                sx={searchListItem(theme)}
                                key={index}
                                onClick={() =>
                                  handleStandardJobSelection(item, formik)
                                }
                                divider
                              >
                                {/* <ListItemText primary={item} /> */}
                                <ListItemText
                                  primary={
                                    item.isModel
                                      ? item.jobCode + " - " + item.modelCode
                                      : item.jobCode
                                  }
                                  secondary={truncateName(
                                    item.jobDescription,
                                    50,
                                  )}
                                  primaryTypographyProps={{
                                    fontWeight: "bold",
                                  }}
                                />
                              </ListItem>
                            ),
                          )}
                        </List>
                      )}
                    </FormControl>
                  </Grid>
                  {/* Memo */}
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Memo"
                      inputProps={{ maxLength: 4000 }}
                      name="memo"
                      value={formik.values.memo ?? ""}
                      onChange={formik.handleChange}
                      error={formik.touched.memo && !!formik.errors.memo}
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                  {/* Standard Job Steps */}
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      inputProps={{ maxLength: 4000 }}
                      label="Standard Job Steps"
                      name="standardJobSteps"
                      value={formik.values.standardJobSteps ?? ""}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.standardJobSteps &&
                        !!formik.errors.standardJobSteps
                      }
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <DialogActions
                  className="segmentFooterAction"
                  sx={{ padding: "2px", mt: "5px" }}
                >
                  {/* <Grid container justifyContent={"flex-end"}> */}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClose}
                    color="inherit"
                    sx={ButtonStyleOutlined}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="small"
                    sx={{ ...ButtonStyle }}
                    variant="contained"
                    type="submit"
                    onClick={async () => {
                      // Surface silent Yup validation errors so the user (and
                      // devs) can tell why submission was blocked instead of
                      // getting no feedback at all.
                      const errors = await formik.validateForm();
                      if (errors && Object.keys(errors).length > 0) {
                        console.warn(
                          "[WorkOrderSegmentUpdate] Validation blocked submit:",
                          errors,
                        );
                        const firstError = Object.values(errors)[0] as string;
                        enqueueSnackbar(
                          firstError ||
                            "Please fix the highlighted fields and try again.",
                          { variant: "warning" },
                        );
                      }
                    }}
                  >
                    Update Segment
                  </Button>
                  {/* </Grid> */}
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WorkOrderSegmentUpdate;
